
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { createDriverAccount, generatePassword } from './authService';
import { sendDriverCredentials } from './smsService';

const COLLECTION_NAME = 'drivers';

// Get all drivers
export const getDrivers = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const drivers = [];
    querySnapshot.forEach((doc) => {
      drivers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return drivers;
  } catch (error) {
    console.error('Error getting drivers: ', error);
    throw error;
  }
};

// Real-time listener for drivers
export const subscribeToDrivers = (callback) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (querySnapshot) => {
    const drivers = [];
    querySnapshot.forEach((doc) => {
      drivers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    callback(drivers);
  }, (error) => {
    console.error('Error in drivers subscription: ', error);
    callback([]);
  });
};

// Add a new driver with Firebase Auth account creation
export const addDriver = async (driverData) => {
  try {
    // Step 1: Create Firebase Auth account
    const authResult = await createDriverAccount(driverData);
    
    // Step 2: Save driver data to Firestore
    const driverDocData = {
      ...driverData,
      uid: authResult.uid, // Store the Firebase Auth UID
      authEmail: authResult.email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      accountStatus: 'active'
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), driverDocData);
    
    // Step 3: Send credentials via SMS
    let smsSent = false;
    try {
      await sendDriverCredentials(driverData, {
        password: authResult.password
      });
      smsSent = true;
      console.log('SMS sent successfully');
    } catch (smsError) {
      console.error('SMS sending failed:', smsError);
      smsSent = false;
      // Don't throw error here - driver account is created successfully
      // We'll just log the SMS failure and continue
    }
    
    return {
      id: docRef.id,
      uid: authResult.uid,
      password: authResult.password, // Return for admin reference
      smsSent: smsSent // Indicate if SMS was sent successfully
    };
  } catch (error) {
    console.error('Error adding driver: ', error);
    throw error;
  }
};

// Update a driver
export const updateDriver = async (driverId, updateData) => {
  try {
    const driverRef = doc(db, COLLECTION_NAME, driverId);
    await updateDoc(driverRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating driver: ', error);
    throw error;
  }
};

// Delete a driver and their Firebase Auth account
export const deleteDriver = async (driverId) => {
  try {
    // First get the driver data to find the UID
    const driverDoc = await getDoc(doc(db, COLLECTION_NAME, driverId));
    
    if (driverDoc.exists()) {
      const driverData = driverDoc.data();
      
      // Delete from Firestore first
      await deleteDoc(doc(db, COLLECTION_NAME, driverId));
      
      // Note: Deleting a user account from Firebase Auth requires admin privileges
      // In a production app, you'd typically make an API call to your backend
      // which would use Firebase Admin SDK to delete the user
      
      // For now, we'll just delete from Firestore
      // TODO: Implement backend API call to delete Firebase Auth user
      console.log(`Driver ${driverId} deleted from Firestore. Auth user ${driverData.uid} should be deleted via backend.`);
    }
  } catch (error) {
    console.error('Error deleting driver: ', error);
    throw error;
  }
};

// Get driver by ID
export const getDriverById = async (driverId) => {
  try {
    const driverDoc = await getDoc(doc(db, COLLECTION_NAME, driverId));
    if (driverDoc.exists()) {
      return {
        id: driverDoc.id,
        ...driverDoc.data()
      };
    } else {
      throw new Error('Driver not found');
    }
  } catch (error) {
    console.error('Error getting driver: ', error);
    throw error;
  }
};

// Reset driver password and send new credentials
export const resetDriverPassword = async (driverId) => {
  try {
    const driverDoc = await getDoc(doc(db, COLLECTION_NAME, driverId));
    
    if (!driverDoc.exists()) {
      throw new Error('Driver not found');
    }
    
    const driverData = driverDoc.data();
    
    // Generate new password
    const newPassword = generatePassword(driverData.name);
    
    // Update password in Firebase Auth (requires backend implementation)
    // TODO: Implement backend API call to update password
    
    // Send new credentials via SMS
    await sendDriverCredentials(driverData, {
      email: driverData.email,
      password: newPassword
    });
    
    // Update driver record with password reset timestamp
    await updateDoc(doc(db, COLLECTION_NAME, driverId), {
      lastPasswordReset: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, newPassword };
  } catch (error) {
    console.error('Error resetting driver password: ', error);
    throw error;
  }
};